import { skipToken } from '@reduxjs/toolkit/query';
import { buttonStyle } from 'components/buttons/buttonStyle';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DateTimeInput from 'components/forms/inputs/rhf-inputs/DateTimeInput';
import { usePaginatedList } from 'components/forms/inputs/rhf-inputs/usePaginatedList';
import PersonOptionLabel from 'components/employee/PersonOptionLabel';
import { Add, LockOutlined } from 'components/icons';
import { FormattedDate } from 'components/misc/FormattedDate';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Combobox, ComboboxContent, ComboboxInput, ComboboxItem, ComboboxList } from 'components/ui/combobox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Field, FieldDescription, FieldError, FieldLabel } from 'components/ui/field';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from 'components/ui/select';
import { Textarea } from 'components/ui/textarea';
import { ReactNode, useId, useMemo, useRef } from 'react';
import { Controller, FieldError as FormFieldError, useForm, UseControllerProps, useWatch } from 'react-hook-form';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import {
    useAddMatterInsiderMutation,
    useGetMatterInsiderEmployeePrefillQuery,
    useLazyGetMatterInsiderEmployeePrefillQuery,
    useUpdateMatterInsiderMutation,
} from 'store/api/matter-insider-list';
import { useGetMinOfficesWithFiltersQuery, useGetOfficeByIdQuery } from 'store/api/offices';
import { EmployeeListItemMinDto } from 'types/employees';
import { MatterInsiderRow, MatterInsiderSave } from 'types/matter-insider-list';
import { formatFullAddress } from 'utils/addressFormatter';
import { cn } from 'utils/cn';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { useEmployeeNameFormatter } from 'utils/nameOrder';
import { useSameNameEmail } from 'utils/sameNameEmail';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { EMPTY, formatLocalMinute, parseLocalMinute, text, toInstant } from './insiderListFormat';

const COUNTRY = ['COUNTRY'];
const READ_EMPLOYEES = ['READ.EMPLOYEES'];
const READ_OFFICES = ['READ.OFFICES'];
const NEW_ADDRESS = 'new';

/**
 * One form for both kinds of row. `isNonEmployee` picks which fields are on screen, and a field that is
 * not rendered is neither validated nor sent.
 */
interface FormValues {
    isNonEmployee: boolean;
    employee: EmployeeListItemMinDto | null;
    firstName: string;
    lastName: string;
    officeId: string | null;
    workPhone: string;
    /** `yyyy-MM-dd`, the wire format, as the date field stores it. */
    dateOfBirth: string | null;
    personalId: string;
    mobilePhone: string;
    functionAndReason: string;
    /** `yyyy-MM-dd HH:mm` in the viewer's time zone, as the date-time field stores it. */
    accessObtained: string;
    accessCeased: string;
    /** An address id of the employee, or {@link NEW_ADDRESS} to type one in. */
    homeAddressId: string | null;
    address1: string;
    cityName: string;
    zipCode: string;
    countryCode: string | null;
}

const BLANK: FormValues = {
    isNonEmployee: false,
    employee: null,
    firstName: '',
    lastName: '',
    officeId: null,
    workPhone: '',
    dateOfBirth: null,
    personalId: '',
    mobilePhone: '',
    functionAndReason: '',
    accessObtained: '',
    accessCeased: '',
    homeAddressId: null,
    address1: '',
    cityName: '',
    zipCode: '',
    countryCode: null,
};

/**
 * Which kinds of person the user can add: the employee picker lists `/v1/employees/min` and the office
 * picker `/v1/offices/min`, and each answers 403 without its read privilege.
 */
export function useAddableKinds(): { employee: boolean; nonEmployee: boolean } {
    return { employee: useHasPermissions(READ_EMPLOYEES), nonEmployee: useHasPermissions(READ_OFFICES) };
}

function initialValues(row: MatterInsiderRow | undefined, canAddEmployee: boolean): FormValues {
    if (!row) {
        return { ...BLANK, isNonEmployee: !canAddEmployee };
    }
    const shared = {
        ...BLANK,
        personalId: row.personalId ?? '',
        functionAndReason: row.functionAndReason ?? '',
        accessObtained: formatLocalMinute(row.accessObtained),
        accessCeased: formatLocalMinute(row.accessCeased),
    };
    if (row.employeeId !== null) {
        return { ...shared, homeAddressId: row.homeAddress?.id ? String(row.homeAddress.id) : null };
    }
    return {
        ...shared,
        isNonEmployee: true,
        firstName: row.firstName ?? '',
        lastName: row.lastName ?? '',
        officeId: row.officeId ? String(row.officeId) : null,
        workPhone: row.workPhone ?? '',
        dateOfBirth: row.dateOfBirth,
        mobilePhone: row.mobilePhone ?? '',
        address1: row.homeAddress?.address1 ?? '',
        cityName: row.homeAddress?.cityName ?? '',
        zipCode: row.homeAddress?.zipCode ?? '',
        countryCode: row.homeAddress?.countryCode ?? null,
    };
}

function orNull(value: string): string | null {
    return value.trim() || null;
}

/** A non-employed person's address is optional, but one started must be finished. */
function hasTypedAddress(values: FormValues): boolean {
    return [values.address1, values.cityName, values.zipCode, values.countryCode ?? ''].some((part) => part.trim());
}

function addressRequired(values: FormValues): boolean {
    return values.isNonEmployee ? hasTypedAddress(values) : values.homeAddressId === NEW_ADDRESS;
}

/** PUT replaces the row, so every editable field is sent, empty ones as null. */
function toSaveDto(
    values: FormValues,
    row: MatterInsiderRow | undefined,
    employeeId: number | null,
    isCeasedLocked: boolean
): MatterInsiderSave {
    const { isNonEmployee } = values;
    const pickedAddressId =
        !isNonEmployee && values.homeAddressId && values.homeAddressId !== NEW_ADDRESS
            ? Number(values.homeAddressId)
            : null;
    return {
        employeeId: isNonEmployee ? null : employeeId,
        firstName: isNonEmployee ? values.firstName.trim() : null,
        lastName: isNonEmployee ? values.lastName.trim() : null,
        officeId: isNonEmployee && values.officeId ? Number(values.officeId) : null,
        workPhone: isNonEmployee ? orNull(values.workPhone) : null,
        functionAndReason: orNull(values.functionAndReason),
        // Validated before submit, so it always parses here.
        accessObtained: toInstant(values.accessObtained, row?.accessObtained) ?? '',
        accessCeased: isCeasedLocked ? null : toInstant(values.accessCeased, row?.accessCeased),
        // An entry the field cannot read is stored as ''.
        dateOfBirth: isNonEmployee ? values.dateOfBirth || null : null,
        personalId: orNull(values.personalId),
        mobilePhone: isNonEmployee ? orNull(values.mobilePhone) : null,
        homeAddressId: pickedAddressId,
        homeAddress: addressRequired(values)
            ? {
                  address1: values.address1.trim(),
                  cityName: values.cityName.trim(),
                  zipCode: orNull(values.zipCode),
                  countryCode: values.countryCode ?? '',
              }
            : null,
    };
}

function FormField({
    label,
    htmlFor,
    required,
    error,
    description,
    className,
    children,
}: {
    label: string;
    htmlFor?: string;
    required?: boolean;
    error?: FormFieldError;
    description?: ReactNode;
    className?: string;
    children: ReactNode;
}) {
    return (
        <Field data-invalid={!!error} className={cn('gap-1.5', className)}>
            <FieldLabel htmlFor={htmlFor}>
                {label}
                {required && <span className="text-destructive">*</span>}
            </FieldLabel>
            {children}
            {description && <FieldDescription className="text-xs">{description}</FieldDescription>}
            <FieldError errors={[error]} />
        </Field>
    );
}

function ProfileValue({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div>
            <dt className="text-grey-600 text-xs">{label}</dt>
            <dd className="m-0 font-medium">{children}</dd>
        </div>
    );
}

interface Props {
    matterId: string;
    /** The row to edit; absent to add one. */
    row?: MatterInsiderRow;
    /** Employees who already have a row, offered disabled in the picker. */
    listedEmployeeIds: ReadonlySet<number>;
    onClose: () => void;
}

/**
 * Adds a person to a case's insider list, or edits a row. An employee's profile fields are copied by the
 * server and shown here as read-only text; a non-employed person's are all typed in, except the company,
 * which follows the office picked. Edits write only the insider-list row, never the employee's profile.
 */
export function InsiderDialog({ matterId, row, listedEmployeeIds, onClose }: Props) {
    const id = useId();
    const formatName = useEmployeeNameFormatter();
    const { classifiers } = useClassifiers({ codes: COUNTRY });
    const [addInsider, { isLoading: isAdding }] = useAddMatterInsiderMutation();
    const [updateInsider, { isLoading: isUpdating }] = useUpdateMatterInsiderMutation();
    const [loadPrefill] = useLazyGetMatterInsiderEmployeePrefillQuery();
    const kinds = useAddableKinds();

    const {
        control,
        register,
        handleSubmit,
        getValues,
        setValue,
        reset,
        formState: { errors },
    } = useForm<FormValues>({ defaultValues: initialValues(row, kinds.employee) });
    const [isNonEmployee, employee, officeId, accessObtained, homeAddressId] = useWatch({
        control,
        name: ['isNonEmployee', 'employee', 'officeId', 'accessObtained', 'homeAddressId'],
    });

    const employeeId = row ? row.employeeId : (employee?.id ?? null);
    const caseRoles = row?.currentCaseRoles ?? [];
    const isCeasedLocked = caseRoles.length > 0;
    // The office and company copied onto a new row are those of the employment valid on this date: the UTC date of
    // the instant, as the server reads it.
    const obtainedDate = parseLocalMinute(accessObtained)?.slice(0, 10);

    const { data: lastPrefill } = useGetMatterInsiderEmployeePrefillQuery(
        employeeId === null ? skipToken : { matterId, employeeId, date: row ? undefined : obtainedDate }
    );
    // While a new date is fetched the previous result stands in; the previous employee's never does.
    const prefill = lastPrefill?.employeeId === employeeId ? lastPrefill : undefined;
    // An edited employee row shows what it stored; the prefill only supplies the address choices.
    const profile = row ?? prefill;

    const { dataToDisplay: employees, handleSearchChange } = usePaginatedList<EmployeeListItemMinDto>({
        fetch: useLazyGetEmployeesListWithFiltersQuery,
        searchField: 'fullName',
        isEnabled: !row && !isNonEmployee,
    });
    const getSameNameEmail = useSameNameEmail(employees);

    // Without READ.OFFICES an edited row keeps its own office, the only item then on offer.
    const { data: offices } = useGetMinOfficesWithFiltersQuery(
        { page: 0, pageSize: 2000 },
        { skip: !isNonEmployee || !kinds.nonEmployee }
    );
    // A row's own office is shown with the company it stored. Only a newly picked office is looked up:
    // the lookup redirects to the not-found page on a 404, which an office deleted since would give.
    const isOfficeChanged = !!officeId && officeId !== String(row?.officeId ?? '');
    const { data: pickedOffice } = useGetOfficeByIdQuery(isNonEmployee && isOfficeChanged ? officeId : skipToken);
    const companyName = isOfficeChanged ? pickedOffice?.company?.name : row?.companyName;

    const officeItems = useMemo(() => {
        const items = (offices?.content ?? []).map((office) => ({ value: String(office.id), label: office.name }));
        const own = row?.officeId ? String(row.officeId) : null;
        return own && !items.some((item) => item.value === own)
            ? [{ value: own, label: row?.officeName ?? '' }, ...items]
            : items;
    }, [offices, row]);

    const addressItems = useMemo(() => {
        // The row's own address stays on offer: the prefill lists none without the personal-details read.
        const own = row?.homeAddress;
        const listed = prefill?.addresses ?? [];
        const addresses = own && !listed.some((address) => address.id === own.id) ? [own, ...listed] : listed;
        return [
            ...addresses.map((address) => ({
                value: String(address.id),
                label: formatFullAddress(address, classifiers),
            })),
            { value: NEW_ADDRESS, label: t('matters.insider-list.add-new-address') },
        ];
    }, [prefill, row, classifiers]);

    const countryOptions = useMemo(
        () => getValidOptionsFromClassifier<string, string>(classifiers['COUNTRY']),
        [classifiers]
    );

    const required = t('forms.field-is-required');
    const fieldId = (name: keyof FormValues) => `${id}-${name}`;

    // The function and reason the last employee pick filled in.
    const prefilledReason = useRef('');

    // Switching keeps what applies to either kind of person and clears the rest, so the two never mix.
    function switchKind(checked: boolean): void {
        const { functionAndReason, accessObtained: obtained, accessCeased } = getValues();
        reset({ ...BLANK, isNonEmployee: checked, functionAndReason, accessObtained: obtained, accessCeased });
    }

    // The national ID starts from the profile and stays editable; the configured function and reason fills the field
    // while it is empty or still holds what the previous pick filled in, so a typed one is never replaced.
    async function pickEmployee(picked: EmployeeListItemMinDto | null): Promise<void> {
        setValue('employee', picked, { shouldValidate: true });
        setValue('homeAddressId', null);
        if (!picked) {
            return;
        }
        try {
            const values = await loadPrefill({ matterId, employeeId: picked.id, date: obtainedDate }, true).unwrap();
            // A later pick may have landed first.
            if (getValues('employee')?.id === picked.id) {
                setValue('personalId', values.personalId ?? '');
                const current = getValues('functionAndReason');
                if (!current.trim() || current === prefilledReason.current) {
                    prefilledReason.current = values.functionAndReason ?? '';
                    setValue('functionAndReason', prefilledReason.current);
                }
            }
        } catch {
            // The API layer has already shown why.
        }
    }

    async function save(values: FormValues): Promise<void> {
        const body = toSaveDto(values, row, employeeId, isCeasedLocked);
        try {
            await (row ? updateInsider({ matterId, id: row.id, body }) : addInsider({ matterId, body })).unwrap();
        } catch {
            // The API layer has already shown the server's reason; the form stays open to fix it.
            return;
        }
        onClose();
    }

    const obtainedRules: UseControllerProps<FormValues, 'accessObtained'>['rules'] = {
        deps: 'accessCeased',
        validate: (value) =>
            !value.trim()
                ? t('matters.insider-list.errors.obtained-required')
                : !!parseLocalMinute(value) || t('matters.insider-list.errors.invalid-time'),
    };
    const ceasedRules: UseControllerProps<FormValues, 'accessCeased'>['rules'] = {
        validate: (value, values) => {
            if (isCeasedLocked || !value.trim()) {
                return true;
            }
            const ceased = toInstant(value, row?.accessCeased);
            if (!ceased) {
                return t('matters.insider-list.errors.invalid-time');
            }
            // The instants that will be sent, so a stored time's seconds count as the server counts them.
            const obtained = toInstant(values.accessObtained, row?.accessObtained);
            return (
                !obtained ||
                Date.parse(ceased) >= Date.parse(obtained) ||
                t('matters.insider-list.errors.ceased-before-obtained')
            );
        },
    };
    const addressRule = {
        validate: (value: string | null, values: FormValues) => !addressRequired(values) || !!value?.trim() || required,
    };

    const accessFields = (
        <>
            <FormField
                label={t('matters.insider-list.function-and-reason')}
                htmlFor={fieldId('functionAndReason')}
                className="col-span-2"
            >
                <Textarea
                    id={fieldId('functionAndReason')}
                    placeholder={t('matters.insider-list.function-and-reason-placeholder')}
                    {...register('functionAndReason')}
                />
            </FormField>
            {/* The date-time fields id their inputs by the field name. */}
            <FormField
                label={t('matters.insider-list.access-obtained')}
                htmlFor="accessObtained"
                required
                error={errors.accessObtained}
            >
                <DateTimeInput control={control} name="accessObtained" rules={obtainedRules} />
            </FormField>
            <FormField
                label={t('matters.insider-list.access-ceased')}
                htmlFor="accessCeased"
                error={errors.accessCeased}
                // The reason is written out rather than hidden in a tooltip.
                description={
                    isCeasedLocked &&
                    t('matters.insider-list.ceased-locked', {
                        role: caseRoles.map((role) => t(`matters.insider-list.roles.${role}`)).join(', '),
                    })
                }
            >
                <DateTimeInput
                    control={control}
                    name="accessCeased"
                    rules={ceasedRules}
                    disabled={isCeasedLocked}
                    placeholder={isCeasedLocked ? EMPTY : undefined}
                />
            </FormField>
        </>
    );

    const addressFields = (
        <div className="grid grid-cols-2 gap-x-4 gap-y-3.5">
            <FormField
                label={t('matters.insider-list.street')}
                htmlFor={fieldId('address1')}
                error={errors.address1}
                className="col-span-2"
            >
                <Input
                    id={fieldId('address1')}
                    aria-invalid={!!errors.address1}
                    {...register('address1', addressRule)}
                />
            </FormField>
            <FormField label={t('matters.insider-list.city')} htmlFor={fieldId('cityName')} error={errors.cityName}>
                <Input
                    id={fieldId('cityName')}
                    aria-invalid={!!errors.cityName}
                    {...register('cityName', addressRule)}
                />
            </FormField>
            <FormField label={t('matters.insider-list.postal-code')} htmlFor={fieldId('zipCode')}>
                <Input id={fieldId('zipCode')} {...register('zipCode')} />
            </FormField>
            <Controller
                control={control}
                name="countryCode"
                rules={addressRule}
                render={({ field, fieldState }) => (
                    <FormField
                        label={t('matters.insider-list.country')}
                        htmlFor={fieldId('countryCode')}
                        error={fieldState.error}
                        className="col-span-2"
                    >
                        <Combobox
                            items={countryOptions}
                            value={countryOptions.find((option) => option.value === field.value) ?? null}
                            onValueChange={(option) => field.onChange(option?.value ?? null)}
                        >
                            <ComboboxInput
                                id={fieldId('countryCode')}
                                ref={field.ref}
                                className="w-full"
                                aria-invalid={!!fieldState.error}
                            />
                            <ComboboxContent>
                                <ComboboxList>
                                    {(option: (typeof countryOptions)[number]) => (
                                        <ComboboxItem key={option.value} value={option}>
                                            {option.label}
                                        </ComboboxItem>
                                    )}
                                </ComboboxList>
                            </ComboboxContent>
                        </Combobox>
                    </FormField>
                )}
            />
        </div>
    );

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-[680px]">
                <DialogHeader>
                    <DialogTitle className="text-base leading-6 font-semibold">
                        {t(row ? 'matters.insider-list.edit' : 'matters.insider-list.add')}
                    </DialogTitle>
                </DialogHeader>
                <form noValidate onSubmit={(event) => void handleSubmit(save)(event)} className="grid gap-6">
                    <div className="grid gap-4.5">
                        {/* Offered only when both kinds can be added; otherwise the dialog opens on the one that can. */}
                        {!row && kinds.employee && kinds.nonEmployee && (
                            <label className="flex w-fit items-center gap-2.5 font-medium">
                                <Checkbox variant="secondary" checked={isNonEmployee} onCheckedChange={switchKind} />
                                {t('matters.insider-list.non-employed-person')}
                            </label>
                        )}

                        {!row && !isNonEmployee && (
                            <Controller
                                control={control}
                                name="employee"
                                rules={{ required }}
                                render={({ field, fieldState }) => (
                                    <FormField
                                        label={t('matters.insider-list.employee')}
                                        htmlFor={fieldId('employee')}
                                        required
                                        error={fieldState.error}
                                    >
                                        <Combobox
                                            items={employees}
                                            value={field.value}
                                            onValueChange={(picked) => void pickEmployee(picked)}
                                            onInputValueChange={handleSearchChange}
                                            // The server filters by the typed name.
                                            filter={null}
                                            itemToStringLabel={(option: EmployeeListItemMinDto) => formatName(option)}
                                            isItemEqualToValue={(option, value) => option.id === value.id}
                                        >
                                            <ComboboxInput
                                                id={fieldId('employee')}
                                                ref={field.ref}
                                                placeholder={t('matters.select-employee')}
                                                className="w-full"
                                                aria-invalid={!!fieldState.error}
                                            />
                                            <ComboboxContent>
                                                <ComboboxList>
                                                    {(option: EmployeeListItemMinDto) => {
                                                        // One row per employee per case, applied at the source.
                                                        const isListed = listedEmployeeIds.has(option.id);
                                                        return (
                                                            <ComboboxItem
                                                                key={option.id}
                                                                value={option}
                                                                disabled={isListed}
                                                            >
                                                                <span className="flex w-full min-w-0 flex-col">
                                                                    <PersonOptionLabel
                                                                        option={option}
                                                                        label={formatName(option)}
                                                                        sameNameEmail={getSameNameEmail(option)}
                                                                    />
                                                                    {isListed && (
                                                                        <span className="text-xs">
                                                                            {t('matters.insider-list.already-listed')}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </ComboboxItem>
                                                        );
                                                    }}
                                                </ComboboxList>
                                            </ComboboxContent>
                                        </Combobox>
                                    </FormField>
                                )}
                            />
                        )}

                        {!isNonEmployee && profile && (
                            // Read-only values as text in a tinted panel, not as disabled inputs.
                            <div className="bg-primary-surface-subtle border-primary-surface grid gap-2.5 rounded-lg border px-4 py-3.5">
                                <div className="text-grey-600 flex items-center gap-1.5 text-xs font-semibold">
                                    <LockOutlined className="size-3.5" />
                                    {t('matters.insider-list.from-profile')}
                                </div>
                                <dl className="m-0 grid grid-cols-3 gap-x-4 gap-y-2.5">
                                    <ProfileValue label={t('matters.insider-list.first-names')}>
                                        {text(profile.firstName)}
                                    </ProfileValue>
                                    <ProfileValue label={t('matters.insider-list.surname')}>
                                        {text(profile.lastName)}
                                    </ProfileValue>
                                    <ProfileValue label={t('matters.insider-list.work-phone')}>
                                        {text(profile.workPhone)}
                                    </ProfileValue>
                                    <ProfileValue label={t('matters.insider-list.company')}>
                                        {text(profile.companyName)}
                                    </ProfileValue>
                                    <ProfileValue label={t('matters.insider-list.date-of-birth')}>
                                        <FormattedDate value={profile.dateOfBirth} fallback={EMPTY} />
                                    </ProfileValue>
                                    <ProfileValue label={t('matters.insider-list.personal-phone')}>
                                        {text(profile.mobilePhone)}
                                    </ProfileValue>
                                </dl>
                            </div>
                        )}

                        {isNonEmployee ? (
                            <>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-3.5">
                                    <FormField
                                        label={t('matters.insider-list.first-names')}
                                        htmlFor={fieldId('firstName')}
                                        required
                                        error={errors.firstName}
                                    >
                                        <Input
                                            id={fieldId('firstName')}
                                            aria-invalid={!!errors.firstName}
                                            {...register('firstName', {
                                                validate: (value) => !!value.trim() || required,
                                            })}
                                        />
                                    </FormField>
                                    <FormField
                                        label={t('matters.insider-list.surname')}
                                        htmlFor={fieldId('lastName')}
                                        required
                                        error={errors.lastName}
                                    >
                                        <Input
                                            id={fieldId('lastName')}
                                            aria-invalid={!!errors.lastName}
                                            {...register('lastName', {
                                                validate: (value) => !!value.trim() || required,
                                            })}
                                        />
                                    </FormField>
                                    <Controller
                                        control={control}
                                        name="officeId"
                                        rules={{ required }}
                                        render={({ field, fieldState }) => (
                                            <FormField
                                                label={t('matters.insider-list.office')}
                                                htmlFor={fieldId('officeId')}
                                                required
                                                error={fieldState.error}
                                            >
                                                <Select
                                                    items={officeItems}
                                                    value={field.value}
                                                    onValueChange={field.onChange}
                                                >
                                                    <SelectTrigger
                                                        id={fieldId('officeId')}
                                                        ref={field.ref}
                                                        className="w-full"
                                                        aria-invalid={!!fieldState.error}
                                                    >
                                                        <SelectValue placeholder={t('matters.select-office')} />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {officeItems.map((item) => (
                                                            <SelectItem key={item.value} value={item.value}>
                                                                {item.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </FormField>
                                        )}
                                    />
                                    <FormField
                                        label={t('matters.insider-list.company')}
                                        htmlFor={fieldId('officeId') + '-company'}
                                        description={t('matters.insider-list.filled-from-office')}
                                    >
                                        <Input
                                            id={fieldId('officeId') + '-company'}
                                            readOnly
                                            tabIndex={-1}
                                            value={companyName ?? ''}
                                            className="bg-grey-100 text-grey-600"
                                        />
                                    </FormField>
                                    {/* The date field ids its input by the field name. */}
                                    <FormField label={t('matters.insider-list.date-of-birth')} htmlFor="dateOfBirth">
                                        <DateInput control={control} name="dateOfBirth" fullWidth />
                                    </FormField>
                                    <FormField
                                        label={t('matters.insider-list.work-phone')}
                                        htmlFor={fieldId('workPhone')}
                                    >
                                        <Input id={fieldId('workPhone')} {...register('workPhone')} />
                                    </FormField>
                                    <FormField
                                        label={t('matters.insider-list.personal-phone')}
                                        htmlFor={fieldId('mobilePhone')}
                                    >
                                        <Input id={fieldId('mobilePhone')} {...register('mobilePhone')} />
                                    </FormField>
                                    <FormField
                                        label={t('matters.insider-list.national-id')}
                                        htmlFor={fieldId('personalId')}
                                    >
                                        <Input id={fieldId('personalId')} {...register('personalId')} />
                                    </FormField>
                                    {accessFields}
                                </div>
                                {/* No saved addresses to choose from, so the fields show open. */}
                                <div className="text-grey-600 border-divider new:text-label new:tracking-[0.06em] border-t pt-3.5 text-xs font-semibold tracking-wide uppercase">
                                    {t('matters.insider-list.home-address')}
                                </div>
                                {addressFields}
                            </>
                        ) : (
                            <>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-3.5">
                                    <FormField
                                        label={t('matters.insider-list.national-id')}
                                        htmlFor={fieldId('personalId')}
                                    >
                                        <Input id={fieldId('personalId')} {...register('personalId')} />
                                    </FormField>
                                    <div />
                                    {accessFields}
                                    <Controller
                                        control={control}
                                        name="homeAddressId"
                                        render={({ field }) => (
                                            <FormField
                                                label={t('matters.insider-list.home-address')}
                                                htmlFor={fieldId('homeAddressId')}
                                                className="col-span-2"
                                            >
                                                {/* A new address is saved to the employee, so it is on offer next time. */}
                                                <Select
                                                    items={addressItems}
                                                    value={field.value}
                                                    onValueChange={field.onChange}
                                                    disabled={employeeId === null}
                                                >
                                                    <SelectTrigger
                                                        id={fieldId('homeAddressId')}
                                                        ref={field.ref}
                                                        className="w-full"
                                                    >
                                                        <SelectValue
                                                            placeholder={t(
                                                                'matters.insider-list.home-address-placeholder'
                                                            )}
                                                        />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {addressItems
                                                            .filter((item) => item.value !== NEW_ADDRESS)
                                                            .map((item) => (
                                                                <SelectItem key={item.value} value={item.value}>
                                                                    {item.label}
                                                                </SelectItem>
                                                            ))}
                                                        <SelectSeparator />
                                                        <SelectItem value={NEW_ADDRESS}>
                                                            <Add />
                                                            {t('matters.insider-list.add-new-address')}
                                                        </SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </FormField>
                                        )}
                                    />
                                </div>
                                {homeAddressId === NEW_ADDRESS && addressFields}
                            </>
                        )}
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="ghost" size="lg" onClick={onClose}>
                            {t('cancel')}
                        </Button>
                        <Button
                            type="submit"
                            {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                            disabled={isAdding || isUpdating}
                        >
                            {row ? t('save') : t('matters.insider-list.add')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
